import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Mail, Phone, Send, X } from "lucide-react";
import { toast } from "sonner";
import { DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { Loading, fmtWhen, money, useAdmin } from "./ui";

const SUGGESTED = ["VIP", "Sensitive skin", "Allergies", "Regular", "Prefers mornings"];

const Notes = ({ c, email }) => {
  const [tags, setTags] = useState(c.tags);
  const [notes, setNotes] = useState(c.notes);
  const [tag, setTag] = useState("");
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const add = (t) => { const v = t.trim(); if (v && !tags.includes(v)) setTags([...tags, v]); setTag(""); };
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/customers/notes?email=${encodeURIComponent(email)}`, { tags, notes });
      qc.invalidateQueries({ queryKey: ["admin", "customers"] });
      toast.success("Customer notes saved");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="cu-block" data-testid="customer-notes">
      <h4>Tags & private notes</h4>
      <div className="cu-tags">
        {tags.map((t) => <span key={t} className="pill cu-tag" data-testid={`customer-tag-${t}`}>{t}<button type="button" aria-label={`Remove ${t}`} onClick={() => setTags(tags.filter((x) => x !== t))}><X size={11} /></button></span>)}
        {SUGGESTED.filter((t) => !tags.includes(t)).map((t) => <button type="button" key={t} className="cu-suggest" onClick={() => add(t)} data-testid={`customer-tag-suggest-${t}`}>+ {t}</button>)}
      </div>
      <input placeholder="Add a tag and press Enter" maxLength={30} value={tag} onChange={(e) => setTag(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(tag); } }} data-testid="customer-tag-input" />
      <textarea rows={3} maxLength={4000} placeholder="Private notes — only staff can see these" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="customer-notes-input" />
      <button className="btn gold" type="button" onClick={save} disabled={busy} data-testid="customer-notes-save-button">{busy ? "Saving…" : "Save notes"}</button>
    </div>
  );
};

const Compose = ({ email, onSent }) => {
  const [f, setF] = useState({ subject: "", text: "" });
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/customers/message?email=${encodeURIComponent(email)}`, f);
      setF({ subject: "", text: "" });
      qc.invalidateQueries({ queryKey: ["admin"] });
      toast.success("Message emailed — tracked in Customer inbox");
      onSent();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form className="cu-block" onSubmit={send} data-testid="customer-message-form">
      <h4>Message customer</h4>
      <input required maxLength={120} placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="customer-message-subject-input" />
      <textarea required rows={4} maxLength={5000} placeholder="Write your message…" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="customer-message-text-input" />
      <button className="btn gold" type="submit" disabled={busy} data-testid="customer-message-send-button">{busy ? <Loader2 size={15} className="spin" /> : <><Send size={14} /> Send email</>}</button>
    </form>
  );
};

const List = ({ title, items, testId, render }) => (
  <div className="cu-block" data-testid={testId}>
    <h4>{title} ({items.length})</h4>
    {items.length === 0 ? <p className="muted-sm">None yet.</p> : <div className="cu-list">{items.map(render)}</div>}
  </div>
);

const Row = ({ main, sub, right, testId }) => (
  <div className="cu-row" data-testid={testId}><div><strong>{main}</strong><small>{sub}</small></div>{right && <span className="pill">{right}</span>}</div>
);

export const CustomerProfile = ({ email }) => {
  const { data: c } = useAdmin(`customers-${email}`, `/admin/customers/detail?email=${encodeURIComponent(email)}`);
  const qc = useQueryClient();
  if (!c) return <><DialogTitle className="ad-msg-title">{email}</DialogTitle><Loading /></>;
  return (
    <div data-testid="customer-profile">
      <DialogTitle className="ad-msg-title">{c.name || email}</DialogTitle>
      <p className="muted-sm" data-testid="customer-profile-contact">{c.email}{c.phone && ` · ${c.phone}`}{c.birthday && ` · Birthday ${c.birthday}`}</p>
      <div className="req-actions">
        <a className="btn light" href={`mailto:${c.email}`} data-testid="customer-email-link"><Mail size={14} /> Email app</a>
        {c.phone && <a className="btn light" href={`tel:${c.phone}`} data-testid="customer-call-link"><Phone size={14} /> Call</a>}
      </div>
      <div className="cu-facts" data-testid="customer-facts">
        <span><b>{money(c.spent)}</b>Total spent</span><span><b>{c.bookings}</b>Bookings</span><span><b>{c.orders}</b>Orders</span>
        <span><b>{fmtWhen(c.first_seen)}</b>First seen</span><span><b>{c.subscribed.join(", ") || "—"}</b>Lists</span>
      </div>
      <Compose email={email} onSent={() => qc.invalidateQueries({ queryKey: ["admin", `customers-${email}`] })} />
      <Notes key={`${c.tags.join()}|${c.notes}`} c={c} email={email} />
      <List title="Services booked" items={c.booking_list} testId="customer-bookings" render={(b) => (
        <Row key={b.id} testId={`customer-booking-${b.id}`} main={b.treatment} sub={`${b.preferred_date} ${b.time_label} · ${b.price}${b.payment_status && b.payment_status !== "none" ? ` · deposit ${b.payment_status}` : ""}`} right={b.status.replace("_", " ")} />
      )} />
      <List title="Products ordered" items={c.order_list} testId="customer-orders" render={(o) => (
        <Row key={o.id} testId={`customer-order-${o.id}`} main={`${o.number} · ${money(o.total)}`} sub={`${fmtWhen(o.created_at)} · ${o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}`} right={o.status.replace("_", " ")} />
      )} />
      <List title="Booking payments" items={c.payment_list} testId="customer-payments" render={(p) => (
        <Row key={p.id} testId={`customer-payment-${p.id}`} main={`${money(p.amount)} · ${p.provider}`} sub={`${fmtWhen(p.paid_at || p.created_at)} · ${p.treatment}`} right={p.status} />
      )} />
      <List title="Messages" items={c.conversation_list} testId="customer-conversations" render={(m) => (
        <Row key={m.id} testId={`customer-conversation-${m.id}`} main={m.subject} sub={`${fmtWhen(m.created_at)} · ${m.thread.length} message${m.thread.length === 1 ? "" : "s"}`} right={m.status.replace("_", " ")} />
      )} />
      {c.review_list.length > 0 && <List title="Reviews" items={c.review_list} testId="customer-reviews" render={(r) => (
        <Row key={r.id} main={`${r.rating}★ ${r.text}`} sub={fmtWhen(r.created_at)} right={r.status} />
      )} />}
    </div>
  );
};
